import { motion } from 'framer-motion'
import { TrendingUp, Users, Award, Zap } from 'lucide-react'
import HeroSection from '../components/HeroSection'
import PodcastCard from '../components/PodcastCard'
import CourseCard from '../components/CourseCard'
import { podcasts, courses, trendingContent, announcements } from '../data/mockData'

function Home() {
  // Check if data exists
  if (!podcasts || !courses || !trendingContent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Loading Content</h1>
          <p className="text-gray-600">Please wait while we load the latest content...</p>
        </div>
      </div>
    );
  }

  // Fix the featured podcasts filtering
  const featuredPodcasts = trendingContent
    .filter((item) => item.type === 'podcast' && item.id)
    .map((item) => podcasts.find((p) => p.id === item.id))
    .filter(Boolean)
    .slice(0, 4); // Limit to 4 for grid

  // Fix the featured courses filtering
  const featuredCourses = trendingContent
    .filter((item) => item.type === 'course' && item.id)
    .map((item) => courses.find((c) => c.id === item.id))
    .filter(Boolean)
    .slice(0, 4); // Limit to 4 for grid

  const stats = [
    { icon: TrendingUp, label: 'Episodes', value: '40+' },
    { icon: Users, label: 'Learners', value: '100+' },
    { icon: Award, label: 'Courses', value: '25+' },
    { icon: Zap, label: 'AI-Powered', value: '100%' }
  ]

  return (
    <div>
      <HeroSection />

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trending Podcasts
            </h2>
            <p className="text-lg text-gray-600">
              Discover the latest AI-generated episodes
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredPodcasts.length > 0 ? (
              featuredPodcasts.map((podcast) => (
                <PodcastCard key={podcast.id} podcast={podcast} />
              ))
            ) : (
              <div className="col-span-4 text-center py-12">
                <div className="text-4xl mb-4">🎧</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Podcasts Available</h3>
                <p className="text-gray-600">Check back soon for new episodes!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Featured Courses
            </h2>
            <p className="text-lg text-gray-600">
              Start your learning journey today
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredCourses.length > 0 ? (
              featuredCourses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))
            ) : (
              // Fallback: Show some default courses if featured courses are empty
              courses.slice(0, 4).map((course) => (
                <CourseCard key={course.id} course={course} />
              ))
            )}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-blue-600 to-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Latest Announcements
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {announcements.map((announcement) => (
              <motion.div
                key={announcement.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 text-white"
              >
                <div className="text-sm text-purple-200 mb-2">
                  {new Date(announcement.date).toLocaleDateString()}
                </div>
                <h3 className="text-xl font-bold mb-3">{announcement.title}</h3>
                <p className="text-purple-100">{announcement.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home